// Pure validation helpers for the payment form on the invoice page.
// No external dependencies so they stay easy to unit-test.

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Card brand config: detection pattern, allowed digit lengths, CVV length,
// and the positions (digit index) where a space is inserted for display.
const CARD_BRANDS = {
  amex: {
    label: "American Express",
    pattern: /^3[47]/,
    lengths: [15],
    cvv: 4,
    gaps: [4, 10],
  },
  visa: {
    label: "Visa",
    pattern: /^4/,
    lengths: [13, 16, 19],
    cvv: 3,
    gaps: [4, 8, 12, 16],
  },
  mastercard: {
    label: "Mastercard",
    pattern: /^(5[1-5]|2[2-7])/,
    lengths: [16],
    cvv: 3,
    gaps: [4, 8, 12],
  },
  discover: {
    label: "Discover",
    pattern: /^(6011|65|64[4-9])/,
    lengths: [16, 19],
    cvv: 3,
    gaps: [4, 8, 12, 16],
  },
};

const DEFAULT_GAPS = [4, 8, 12, 16];
const DEFAULT_MAX_LEN = 19;

const onlyDigits = (value) => (value || "").replace(/\D/g, "");

export const detectCardBrand = (value) => {
  const digits = onlyDigits(value);
  return (
    Object.keys(CARD_BRANDS).find((key) =>
      CARD_BRANDS[key].pattern.test(digits),
    ) || null
  );
};

// Human-readable brand name for display next to the card number field.
export const cardBrandLabel = (value) => {
  const brand = detectCardBrand(value);
  return brand ? CARD_BRANDS[brand].label : "";
};

// Insert spaces per the detected brand and cap length so typing stays tidy.
export const formatCardNumber = (value) => {
  const brand = detectCardBrand(value);
  const gaps = brand ? CARD_BRANDS[brand].gaps : DEFAULT_GAPS;
  const maxLen = brand ? Math.max(...CARD_BRANDS[brand].lengths) : DEFAULT_MAX_LEN;
  const digits = onlyDigits(value).slice(0, maxLen);

  let formatted = "";
  for (let i = 0; i < digits.length; i += 1) {
    if (gaps.includes(i)) formatted += " ";
    formatted += digits[i];
  }
  return formatted;
};

// Standard Luhn checksum — catches most mistyped card numbers.
export const luhnCheck = (value) => {
  const digits = onlyDigits(value);
  if (!digits) return false;

  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let digit = parseInt(digits[i], 10);
    if (double) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    double = !double;
  }
  return sum % 10 === 0;
};

export const validateEmail = (email) => {
  const value = (email || "").trim();
  if (!value) return "Email is required";
  if (!EMAIL_REGEX.test(value)) return "Enter a valid email address";
  return "";
};

export const validateCardNumber = (value) => {
  const digits = onlyDigits(value);
  if (!digits) return "Card number is required";

  const brand = detectCardBrand(digits);
  const validLengths = brand
    ? CARD_BRANDS[brand].lengths
    : [13, 14, 15, 16, 17, 18, 19];

  if (!validLengths.includes(digits.length)) return "Card number is incomplete";
  if (!luhnCheck(digits)) return "Card number is invalid";
  return "";
};

export const validateCvv = (cvv, cardNumber) => {
  const value = (cvv || "").trim();
  if (!value) return "CVV is required";
  if (!/^\d+$/.test(value)) return "CVV must be digits only";

  const brand = detectCardBrand(cardNumber);
  const expected = brand ? CARD_BRANDS[brand].cvv : null;
  if (expected ? value.length !== expected : value.length < 3 || value.length > 4) {
    return `CVV must be ${expected || "3 or 4"} digits`;
  }
  return "";
};

export const validateExpiry = (month, year) => {
  if (!month || !year) return "Select the card's expiry date";

  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  if (y < currentYear || (y === currentYear && m < currentMonth)) {
    return "Card has expired";
  }
  return "";
};

// Required-field labels for the plain "X is required" checks.
const REQUIRED_LABELS = {
  first_name: "First name",
  last_name: "Last name",
  phone: "Phone",
  country: "Country",
  state: "State",
  city: "City",
  address: "Address",
  zip: "Zip code",
  card_name: "Name on card",
};

// Validate a single field. Expiry maps to the shared "expiry" error key.
export const validateField = (name, data) => {
  switch (name) {
    case "email":
      return validateEmail(data.email);
    case "card_number":
      return validateCardNumber(data.card_number);
    case "cvv":
      return validateCvv(data.cvv, data.card_number);
    case "expiryMonth":
    case "expiryYear":
      return validateExpiry(data.expiryMonth, data.expiryYear);
    default:
      return (data[name] || "").trim()
        ? ""
        : `${REQUIRED_LABELS[name] || "This field"} is required`;
  }
};

// Validate the whole form, returning an object of { fieldKey: errorMessage }.
export const validatePaymentForm = (data) => {
  const errors = {};

  Object.keys(REQUIRED_LABELS).forEach((name) => {
    const message = validateField(name, data);
    if (message) errors[name] = message;
  });

  const emailError = validateEmail(data.email);
  if (emailError) errors.email = emailError;

  const cardError = validateCardNumber(data.card_number);
  if (cardError) errors.card_number = cardError;

  const cvvError = validateCvv(data.cvv, data.card_number);
  if (cvvError) errors.cvv = cvvError;

  const expiryError = validateExpiry(data.expiryMonth, data.expiryYear);
  if (expiryError) errors.expiry = expiryError;

  return errors;
};
